import { useState, useEffect, useMemo, useRef } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { FiChevronLeft, FiChevronRight, FiZoomIn, FiZoomOut, FiDownload, FiX, FiEdit3, FiTrash2, FiSave, FiMaximize, FiMinimize } from 'react-icons/fi'
import { getStorageUrl, STORAGE_KEYS, API_BASE_URL } from '../../utils/constants'
import { useAuth } from '../../context/AuthContext'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import './PDFViewer.css'

// Set up PDF.js worker
// Use the version from pdfjs to ensure it matches react-pdf's bundled version
// The .mjs extension is required for ES modules
const pdfjsVersion = pdfjs.version || '5.4.296'
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsVersion}/build/pdf.worker.min.mjs`

const PDFViewer = ({ url, fileName, onClose, signedUrl }) => {
  const { user } = useAuth()
  const [numPages, setNumPages] = useState(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [scale, setScale] = useState(1.0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [pdfFile, setPdfFile] = useState(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [drawingColor, setDrawingColor] = useState('#FF0000')
  const [drawingWidth, setDrawingWidth] = useState(2)
  const [drawings, setDrawings] = useState({}) // Store drawings per page: { pageNumber: [paths] }
  const canvasRef = useRef(null)
  const pageContainerRef = useRef(null)
  const containerRef = useRef(null)
  const isDrawingRef = useRef(false)
  const currentPathRef = useRef([])
  const [showScreenshotWarning, setShowScreenshotWarning] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const blobUrlRef = useRef(null) // Store blob URL for cleanup
  
  // Process the URL to ensure it's a proper storage path
  // Always convert storage URLs to API endpoint to bypass CORS
  const pdfUrl = useMemo(() => {
    // Helper function to convert storage URL to API endpoint
    // This bypasses CORS by using the API endpoint which has proper CORS headers
    const convertToApiPath = (fullUrl) => {
      try {
        const urlObj = new URL(fullUrl)
        // If it's a storage URL from admin.3afer.net or 3afer.net
        if (urlObj.hostname.includes('admin.3afer.net') || urlObj.hostname.includes('3afer.net')) {
          // Extract the path after /storage/
          let path = urlObj.pathname
          if (path.startsWith('/storage/')) {
            path = path.replace('/storage/', '')
          } else if (path.startsWith('/storage')) {
            path = path.replace('/storage', '')
          }
          
          // Remove leading slash
          path = path.startsWith('/') ? path.slice(1) : path
          
          // Construct API URL - API_BASE_URL already includes /api
          // e.g., https://admin.3afer.net/api/storage/materials/...
          return `${API_BASE_URL}/storage/${path}`
        }
      } catch (e) {
        console.warn('Failed to parse URL for API conversion:', e)
        // If URL parsing fails, return original
      }
      return fullUrl
    }
    
    // Always prefer signedUrl if available (should have proper CORS headers)
    if (signedUrl) {
      if (signedUrl.startsWith('http://') || signedUrl.startsWith('https://')) {
        // Convert signed URL to API endpoint
        return convertToApiPath(signedUrl)
      }
      // If signedUrl is a relative path, use getStorageUrl then convert
      const storageUrl = getStorageUrl(signedUrl)
      return convertToApiPath(storageUrl)
    }
    
    // Fallback to regular URL
    if (url) {
      if (url.startsWith('http://') || url.startsWith('https://')) {
        // Convert to API endpoint
        return convertToApiPath(url)
      }
      // If url is a relative path, use getStorageUrl then convert
      const storageUrl = getStorageUrl(url)
      return convertToApiPath(storageUrl)
    }
    
    return null
  }, [signedUrl, url])

  // Fullscreen handling
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }
    
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    document.addEventListener('mozfullscreenchange', handleFullscreenChange)
    document.addEventListener('MSFullscreenChange', handleFullscreenChange)
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange)
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange)
    }
  }, [])

  const toggleFullscreen = () => {
    const container = containerRef.current
    if (!container) return

    if (!isFullscreen) {
      // Enter fullscreen
      if (container.requestFullscreen) {
        container.requestFullscreen()
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen()
      } else if (container.mozRequestFullScreen) {
        container.mozRequestFullScreen()
      } else if (container.msRequestFullscreen) {
        container.msRequestFullscreen()
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen()
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen()
      }
    }
  }

  // Download and screenshot protection
  useEffect(() => {
    // Detect print dialog
    const handleBeforePrint = () => {
      const container = document.querySelector('.pdf-viewer-container')
      if (container) {
        container.style.filter = 'blur(20px)'
        setShowScreenshotWarning(true)
      }
    }

    const handleAfterPrint = () => {
      const container = document.querySelector('.pdf-viewer-container')
      if (container) {
        container.style.filter = ''
        setShowScreenshotWarning(false)
      }
    }

    // Detect print media query
    const printMediaQuery = window.matchMedia('print')
    const handlePrintChange = (mq) => {
      if (mq.matches) {
        handleBeforePrint()
      } else {
        handleAfterPrint()
      }
    }
    printMediaQuery.addEventListener('change', handlePrintChange)

    // Block print dialog
    window.addEventListener('beforeprint', handleBeforePrint)
    window.addEventListener('afterprint', handleAfterPrint)

    // Disable right-click context menu
    const handleContextMenu = (e) => {
      e.preventDefault()
      return false
    }

    // Disable keyboard shortcuts (Ctrl+S, Ctrl+P, Print Screen, etc.)
    const handleKeyDown = (e) => {
      // Block Ctrl+S (Save), Ctrl+P (Print), Ctrl+Shift+I (DevTools), F12, Print Screen
      if (
        (e.ctrlKey && (e.key === 's' || e.key === 'S' || e.key === 'p' || e.key === 'P')) ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'i')) ||
        e.key === 'F12' ||
        e.keyCode === 44 || // Print Screen key code
        e.key === 'PrintScreen' || 
        e.key === 'Print' ||
        (e.key === 'F13') // Some systems use F13 for Print Screen
      ) {
        e.preventDefault()
        e.stopPropagation()
        return false
      }
    }

    // Additional Print Screen detection
    const handleKeyUp = (e) => {
      if (e.keyCode === 44 || e.key === 'PrintScreen' || e.key === 'Print') {
        e.preventDefault()
        e.stopPropagation()
        // Show warning and blur content briefly
        const container = document.querySelector('.pdf-viewer-container')
        if (container) {
          container.style.filter = 'blur(10px)'
          setShowScreenshotWarning(true)
          setTimeout(() => {
            if (container) container.style.filter = ''
            setShowScreenshotWarning(false)
          }, 2000)
        }
        return false
      }
    }

    // Detect Alt+PrintScreen (Windows) or Shift+Command+3/4 (Mac)
    const handleKeyPress = (e) => {
      if (
        (e.altKey && e.keyCode === 44) ||
        (e.shiftKey && e.metaKey && (e.keyCode === 51 || e.keyCode === 52))
      ) {
        e.preventDefault()
        e.stopPropagation()
        const container = document.querySelector('.pdf-viewer-container')
        if (container) {
          container.style.filter = 'blur(10px)'
          setShowScreenshotWarning(true)
          setTimeout(() => {
            if (container) container.style.filter = ''
            setShowScreenshotWarning(false)
          }, 2000)
        }
        return false
      }
    }

    // Disable text selection
    const handleSelectStart = (e) => {
      // Allow selection only for UI elements, not PDF content
      if (e.target.closest('.pdf-viewer-header') || 
          e.target.closest('.pdf-viewer-controls') ||
          e.target.closest('.pdf-action-btn')) {
        return true
      }
      e.preventDefault()
      return false
    }

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu)
    document.addEventListener('keydown', handleKeyDown, true) // Use capture phase
    document.addEventListener('keyup', handleKeyUp, true) // Use capture phase
    document.addEventListener('keypress', handleKeyPress, true)
    window.addEventListener('keydown', handleKeyDown, true)
    window.addEventListener('keyup', handleKeyUp, true)
    window.addEventListener('keypress', handleKeyPress, true)
    document.addEventListener('selectstart', handleSelectStart)

    return () => {
      printMediaQuery.removeEventListener('change', handlePrintChange)
      window.removeEventListener('beforeprint', handleBeforePrint)
      window.removeEventListener('afterprint', handleAfterPrint)
      document.removeEventListener('contextmenu', handleContextMenu)
      document.removeEventListener('keydown', handleKeyDown, true)
      document.removeEventListener('keyup', handleKeyUp, true)
      document.removeEventListener('keypress', handleKeyPress, true)
      window.removeEventListener('keydown', handleKeyDown, true)
      window.removeEventListener('keyup', handleKeyUp, true)
      window.removeEventListener('keypress', handleKeyPress, true)
      document.removeEventListener('selectstart', handleSelectStart)
    }
  }, [])

  // Set PDF file when URL is available
  useEffect(() => {
    // Cleanup previous blob URL
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current)
      blobUrlRef.current = null
    }
    
    if (!pdfUrl) {
      setError('لا يوجد رابط للملف')
      setLoading(false)
      setPdfFile(null)
      return
    }

    // Reset states when URL changes
    setError(null)
    setLoading(true)
    setPageNumber(1) // Reset to first page when PDF changes
    setNumPages(null) // Reset page count
    
    // Always fetch as blob to handle CORS issues
    // The pdfUrl should already be converted to use the API endpoint
    const loadPdf = async () => {
      // Always try to fetch as blob first - pdfUrl should be the API endpoint
      try {
        // Get auth token - always include it for API requests
        const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
        const headers = {
          'Accept': 'application/pdf',
        }
        
        // Always add Authorization header if token exists (API endpoint requires it)
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }
        
        console.log('Fetching PDF from:', pdfUrl)
        
        const response = await fetch(pdfUrl, {
          method: 'GET',
          credentials: 'include', // Include cookies for auth
          headers: headers,
          mode: 'cors', // Explicitly set CORS mode
        })
        
        if (response.ok) {
          const blob = await response.blob()
          const blobUrl = URL.createObjectURL(blob)
          blobUrlRef.current = blobUrl
          setPdfFile(blobUrl)
          console.log('PDF fetched successfully as blob')
          return
        } else {
          // If response is not OK, log for debugging
          console.error('PDF fetch response not OK:', response.status, response.statusText, response)
          // Try to get error message from response
          try {
            const errorText = await response.text()
            console.error('Response body:', errorText)
          } catch (e) {
            // Ignore
          }
        }
      } catch (fetchError) {
        // CORS or network error - log and fall through to direct URL
        console.error('Failed to fetch PDF as blob:', fetchError)
        console.error('PDF URL was:', pdfUrl)
        
        // Fall through to use direct URL (react-pdf will handle it)
        // This might still fail with CORS, but react-pdf will show the error
      }
      
      // Fallback: use direct URL (react-pdf will handle it)
      // Note: This will likely fail with CORS if the API endpoint fetch failed
      console.log('Falling back to direct URL:', pdfUrl)
      setPdfFile(pdfUrl)
    }
    
    loadPdf()
    
    // Cleanup on unmount
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
        blobUrlRef.current = null
      }
    }
  }, [pdfUrl, signedUrl, url])

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages)
    setLoading(false)
    setError(null)
  }

  const onDocumentLoadError = (error) => {
    console.error('PDF load error:', error)
    
    // Check if it's a CORS or fetch error
    const isCorsError = error.message && (
      error.message.includes('CORS') || 
      error.message.includes('Failed to fetch') ||
      error.message.includes('fetch') ||
      error.message.includes('Access-Control') ||
      error.name === 'UnknownErrorException' ||
      error.message.includes('ERR_FAILED') ||
      error.message.includes('net::ERR')
    )
    
    if (isCorsError) {
      // CORS error - show message with option to open in new tab
      // Don't use iframe as it will also be blocked by X-Frame-Options
      setError('CORS_ERROR') // Special error code for CORS
      setLoading(false)
      return
    }
    
    let errorMessage = 'فشل تحميل ملف PDF - الملف قد يكون تالفاً أو غير صالح'
    
    // Check for specific error types
    if (error.message) {
      if (error.message.includes('Invalid PDF') || error.message.includes('InvalidPDFException')) {
        errorMessage = 'الملف ليس ملف PDF صالح. قد يكون الملف تالفاً أو الرابط غير صحيح.'
      } else if (error.message.includes('404')) {
        errorMessage = 'الملف غير موجود (404)'
      } else if (error.message.includes('403')) {
        errorMessage = 'غير مصرح بالوصول إلى هذا الملف (403)'
      } else if (error.message.includes('network')) {
        errorMessage = 'خطأ في الاتصال بالخادم. يرجى المحاولة مرة أخرى.'
      }
    }
    
    setError(errorMessage)
    setLoading(false)
  }

  const goToPrevPage = () => {
    setPageNumber(prev => Math.max(1, prev - 1))
  }

  const goToNextPage = () => {
    setPageNumber(prev => Math.min(numPages, prev + 1))
  }

  const zoomIn = () => {
    setScale(prev => Math.min(3.0, prev + 0.25))
  }

  const zoomOut = () => {
    setScale(prev => Math.max(0.5, prev - 0.25))
  }

  const handleDownload = () => {
    if (!pdfUrl) return
    const link = document.createElement('a')
    link.href = pdfUrl
    link.download = fileName || 'document.pdf'
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Drawing functionality
  useEffect(() => {
    if (!isDrawing || !canvasRef.current || !pageContainerRef.current) return

    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const container = pageContainerRef.current

    // Set canvas size to match PDF page
    const updateCanvasSize = (preserveDrawing = false) => {
      const pageElement = container.querySelector('.react-pdf__Page__canvas')
      const pageWrapper = container.querySelector('.pdf-page-wrapper')
      
      if (pageElement && pageWrapper) {
        const pageRect = pageElement.getBoundingClientRect()
        const wrapperRect = pageWrapper.getBoundingClientRect()
        
        // Save current canvas content if we need to preserve it
        let imageData = null
        if (preserveDrawing && canvas.width > 0 && canvas.height > 0) {
          imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        }
        
        // Set canvas dimensions to match PDF page (this clears the canvas)
        const needsResize = canvas.width !== pageRect.width || canvas.height !== pageRect.height
        if (needsResize) {
          canvas.width = pageRect.width
          canvas.height = pageRect.height
        }
        
        // Restore canvas content if we preserved it
        if (imageData && needsResize) {
          // Note: We can't directly restore if size changed, so we'll redraw instead
          // The redraw will happen below
        }
        
        // Position canvas absolutely within the wrapper
        canvas.style.width = `${pageRect.width}px`
        canvas.style.height = `${pageRect.height}px`
        canvas.style.position = 'absolute'
        canvas.style.top = `${pageRect.top - wrapperRect.top}px`
        canvas.style.left = `${pageRect.left - wrapperRect.left}px`
        canvas.style.pointerEvents = 'auto'
        canvas.style.zIndex = '10'
        canvas.style.cursor = 'crosshair'
        
        // Redraw existing paths for current page
        redrawPaths(ctx, preserveDrawing)
      }
    }

    const redrawPaths = (context, preserveCurrent = false) => {
      const pageDrawings = drawings[pageNumber] || []
      
      // Only clear if we're not preserving current drawing
      if (!preserveCurrent) {
        context.clearRect(0, 0, canvas.width, canvas.height)
      }
      
      // Redraw all saved paths
      pageDrawings.forEach(path => {
        if (path.length < 2) return
        context.beginPath()
        context.moveTo(path[0].x, path[0].y)
        for (let i = 1; i < path.length; i++) {
          context.lineTo(path[i].x, path[i].y)
        }
        const pathColor = path[0]?.color || path.color || drawingColor
        const pathWidth = path[0]?.width || path.width || drawingWidth
        context.strokeStyle = pathColor
        context.lineWidth = pathWidth
        context.lineCap = 'round'
        context.lineJoin = 'round'
        context.stroke()
      })
    }

    // Initial setup - only clear if this is a new page or scale change
    const isInitialSetup = !canvas.width || !canvas.height
    updateCanvasSize(!isInitialSetup)
    
    // Watch for resize/scale changes - but don't clear on resize observer triggers
    // Only clear when dimensions actually change
    let lastWidth = 0
    let lastHeight = 0
    
    const resizeObserver = new ResizeObserver(() => {
      const pageElement = container.querySelector('.react-pdf__Page__canvas')
      if (pageElement) {
        const rect = pageElement.getBoundingClientRect()
        // Only update if dimensions actually changed
        if (rect.width !== lastWidth || rect.height !== lastHeight) {
          lastWidth = rect.width
          lastHeight = rect.height
          updateCanvasSize(true) // Preserve current drawing
        }
      }
    })
    
    if (container) {
      resizeObserver.observe(container)
    }
    
    // Also update on scale changes
    const pageElement = container.querySelector('.react-pdf__Page__canvas')
    if (pageElement) {
      resizeObserver.observe(pageElement)
      const rect = pageElement.getBoundingClientRect()
      lastWidth = rect.width
      lastHeight = rect.height
    }

    const startDrawing = (e) => {
      isDrawingRef.current = true
      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      currentPathRef.current = [{ x, y, color: drawingColor, width: drawingWidth }]
    }

    const draw = (e) => {
      if (!isDrawingRef.current) return
      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      
      const lastPoint = currentPathRef.current[currentPathRef.current.length - 1]
      currentPathRef.current.push({ x, y, color: drawingColor, width: drawingWidth })

      ctx.beginPath()
      ctx.moveTo(lastPoint.x, lastPoint.y)
      ctx.lineTo(x, y)
      ctx.strokeStyle = drawingColor
      ctx.lineWidth = drawingWidth
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.stroke()
    }

    const stopDrawing = () => {
      if (isDrawingRef.current && currentPathRef.current.length > 0) {
        // Save the current path to state
        // The drawing is already visible on the canvas, so we just save it
        const pathToSave = [...currentPathRef.current]
        setDrawings(prev => {
          const newDrawings = {
            ...prev,
            [pageNumber]: [...(prev[pageNumber] || []), pathToSave]
          }
          return newDrawings
        })
        currentPathRef.current = []
      }
      isDrawingRef.current = false
    }

    canvas.addEventListener('mousedown', startDrawing)
    canvas.addEventListener('mousemove', draw)
    canvas.addEventListener('mouseup', stopDrawing)
    canvas.addEventListener('mouseleave', stopDrawing)

    // Touch events for mobile
    canvas.addEventListener('touchstart', (e) => {
      e.preventDefault()
      const touch = e.touches[0]
      startDrawing({ clientX: touch.clientX, clientY: touch.clientY })
    })
    canvas.addEventListener('touchmove', (e) => {
      e.preventDefault()
      const touch = e.touches[0]
      draw({ clientX: touch.clientX, clientY: touch.clientY })
    })
    canvas.addEventListener('touchend', (e) => {
      e.preventDefault()
      stopDrawing()
    })

    return () => {
      canvas.removeEventListener('mousedown', startDrawing)
      canvas.removeEventListener('mousemove', draw)
      canvas.removeEventListener('mouseup', stopDrawing)
      canvas.removeEventListener('mouseleave', stopDrawing)
      canvas.removeEventListener('touchstart', startDrawing)
      canvas.removeEventListener('touchmove', draw)
      canvas.removeEventListener('touchend', stopDrawing)
      resizeObserver.disconnect()
    }
  }, [isDrawing, pageNumber, drawingColor, drawingWidth, drawings])

  // Track previous page/scale to detect actual changes
  const prevPageRef = useRef(pageNumber)
  const prevScaleRef = useRef(scale)
  const drawingsRef = useRef(drawings)

  // Update drawings ref when it changes (but don't trigger redraw)
  useEffect(() => {
    drawingsRef.current = drawings
  }, [drawings])

  // Redraw canvas when page changes or scale changes (not when drawings update)
  useEffect(() => {
    if (!canvasRef.current || !isDrawing || !pageContainerRef.current) return
    
    const pageChanged = prevPageRef.current !== pageNumber
    const scaleChanged = prevScaleRef.current !== scale
    
    prevPageRef.current = pageNumber
    prevScaleRef.current = scale
    
    const updateCanvas = () => {
      const canvas = canvasRef.current
      const ctx = canvas.getContext('2d')
      const container = pageContainerRef.current
      const pageElement = container?.querySelector('.react-pdf__Page__canvas')
      const pageWrapper = container?.querySelector('.pdf-page-wrapper')
      
      if (pageElement && pageWrapper) {
        const pageRect = pageElement.getBoundingClientRect()
        const wrapperRect = pageWrapper.getBoundingClientRect()
        
        // Only resize if dimensions changed
        const needsResize = canvas.width !== pageRect.width || canvas.height !== pageRect.height
        
        if (needsResize) {
          canvas.width = pageRect.width
          canvas.height = pageRect.height
        }
        
        canvas.style.width = `${pageRect.width}px`
        canvas.style.height = `${pageRect.height}px`
        canvas.style.position = 'absolute'
        canvas.style.top = `${pageRect.top - wrapperRect.top}px`
        canvas.style.left = `${pageRect.left - wrapperRect.left}px`
        
        // Only clear if page/scale changed or if we resized
        if (pageChanged || scaleChanged || needsResize) {
          ctx.clearRect(0, 0, canvas.width, canvas.height)
        }
        
        // Redraw all saved paths for current page (use ref to get latest drawings)
        const pageDrawings = drawingsRef.current[pageNumber] || []
        pageDrawings.forEach(path => {
          if (path.length < 2) return
          ctx.beginPath()
          ctx.moveTo(path[0].x, path[0].y)
          for (let i = 1; i < path.length; i++) {
            ctx.lineTo(path[i].x, path[i].y)
          }
          const pathColor = path[0]?.color || path.color || drawingColor
          const pathWidth = path[0]?.width || path.width || drawingWidth
          ctx.strokeStyle = pathColor
          ctx.lineWidth = pathWidth
          ctx.lineCap = 'round'
          ctx.lineJoin = 'round'
          ctx.stroke()
        })
      }
    }
    
    // Use setTimeout to ensure DOM is updated after PDF renders
    const timeout = setTimeout(updateCanvas, 100)
    return () => clearTimeout(timeout)
  }, [pageNumber, scale, isDrawing, drawingColor, drawingWidth]) // Removed drawings from deps

  const clearCurrentPage = () => {
    setDrawings(prev => {
      const newDrawings = { ...prev }
      delete newDrawings[pageNumber]
      return newDrawings
    })
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d')
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height)
    }
  }

  const toggleDrawing = () => {
    setIsDrawing(prev => !prev)
  }

  // Memoize options to prevent unnecessary reloads
  const pdfOptions = useMemo(() => ({
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjsVersion}/cmaps/`,
    cMapPacked: true,
    httpHeaders: {
      'Accept': 'application/pdf',
    },
    withCredentials: false,
    standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjsVersion}/standard_fonts/`,
  }), [pdfjsVersion])

  return (
    <div className="pdf-viewer-overlay" onClick={onClose}>
      {showScreenshotWarning && (
        <div className="screenshot-warning">
          <div className="warning-content">
            <h3>⚠️ Screenshots are not allowed</h3>
            <p>This content is protected</p>
          </div>
        </div>
      )}
      <div className="pdf-viewer-container" ref={containerRef} onClick={(e) => e.stopPropagation()}>
        <div className="pdf-viewer-header">
          <h3 className="pdf-viewer-title">{fileName || 'مستند PDF'}</h3>
          
          <div className="pdf-viewer-controls-group">
            <div className="pdf-nav-controls">
              <button
                className="pdf-nav-btn"
                onClick={goToPrevPage}
                disabled={pageNumber <= 1}
              >
                <FiChevronLeft />
              </button>
              <span className="pdf-page-info">
                صفحة {pageNumber} من {numPages || '...'}
              </span>
              <button
                className="pdf-nav-btn"
                onClick={goToNextPage}
                disabled={pageNumber >= numPages}
              >
                <FiChevronRight />
              </button>
            </div>

            <div className="pdf-zoom-controls">
              <button className="pdf-zoom-btn" onClick={zoomOut} disabled={scale <= 0.5}>
                <FiZoomOut />
              </button>
              <span className="pdf-zoom-level">{Math.round(scale * 100)}%</span>
              <button className="pdf-zoom-btn" onClick={zoomIn} disabled={scale >= 3.0}>
                <FiZoomIn />
              </button>
            </div>
          </div>

          <div className="pdf-viewer-actions">
            <button 
              className={`pdf-action-btn ${isDrawing ? 'active' : ''}`} 
              onClick={toggleDrawing} 
              title={isDrawing ? 'إيقاف الرسم' : 'الرسم'}
            >
              <FiEdit3 />
            </button>
            {isDrawing && (
              <>
                <input
                  type="color"
                  value={drawingColor}
                  onChange={(e) => setDrawingColor(e.target.value)}
                  className="pdf-color-picker"
                  title="اختر لون الرسم"
                />
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={drawingWidth}
                  onChange={(e) => setDrawingWidth(parseInt(e.target.value))}
                  className="pdf-brush-size"
                  title="حجم الفرشاة"
                />
                <button className="pdf-action-btn" onClick={clearCurrentPage} title="مسح الصفحة الحالية">
                  <FiTrash2 />
                </button>
              </>
            )}
            <button className="pdf-action-btn" onClick={toggleFullscreen} title={isFullscreen ? 'خروج من وضع ملء الشاشة' : 'ملء الشاشة'}>
              {isFullscreen ? <FiMinimize /> : <FiMaximize />}
            </button>
            <button className="pdf-action-btn" onClick={onClose} title="إغلاق">
              <FiX />
            </button>
          </div>
        </div>

        <div className="pdf-viewer-content">
          {loading && (
            <div className="pdf-loading">
              <p>جاري تحميل المستند...</p>
            </div>
          )}
          {error && (
            <div className="pdf-error">
              <p>{error === 'CORS_ERROR' 
                ? 'لا يمكن تحميل الملف بسبب قيود الأمان (CORS). يرجى فتحه في نافذة جديدة.' 
                : error}
              </p>
              {pdfUrl && (
                <div style={{ marginTop: '16px', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
                  <button
                    onClick={() => window.open(pdfUrl, '_blank')}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: 'var(--color-primary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#1e5a8a'
                      e.target.style.transform = 'translateY(-2px)'
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'var(--color-primary)'
                      e.target.style.transform = 'translateY(0)'
                    }}
                  >
                    فتح الملف في نافذة جديدة
                  </button>
                  {error === 'CORS_ERROR' && (
                    <p style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', margin: 0 }}>
                      ملاحظة: قد تحتاج إلى تسجيل الدخول في النافذة الجديدة
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          {!error && pdfFile && (
            <div className="pdf-page-container" ref={pageContainerRef}>
              <Document
                file={pdfFile}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                options={pdfOptions}
                loading={
                  <div className="pdf-loading">
                    <p>جاري تحميل المستند...</p>
                  </div>
                }
              >
                <div className="pdf-page-wrapper" style={{ position: 'relative', display: 'inline-block' }}>
                  <Page
                    pageNumber={pageNumber}
                    scale={scale}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                    className="pdf-page"
                  />
                  {/* Watermark - repeated pattern across PDF page */}
                  {user?.email && (
                    <div className="pdf-watermark-container">
                      {Array.from({ length: 25 }).map((_, index) => {
                        const rows = 5
                        const cols = 4
                        const row = Math.floor(index / cols)
                        const col = index % cols
                        return (
                          <div 
                            key={index}
                            className="pdf-watermark"
                            style={{
                              position: 'absolute',
                              top: `${(row * (100 / rows)) + (100 / (rows * 2))}%`,
                              left: `${(col * (100 / cols)) + (100 / (cols * 2))}%`,
                              transform: 'translate(-50%, -50%) rotate(-45deg)',
                            }}
                          >
                            {user.email}
                          </div>
                        )
                      })}
                    </div>
                  )}
                  {/* Drawing Canvas - only show when drawing is enabled */}
                  <canvas
                    ref={canvasRef}
                    className="pdf-drawing-canvas"
                    style={{ display: isDrawing ? 'block' : 'none' }}
                  />
                  
                  {/* Additional security overlay - makes screenshots less useful */}
                  <div className="pdf-security-overlay"></div>
                </div>
              </Document>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PDFViewer

